import type {
  AcaoAutorizacaoAr,
  AutorizacaoArEnvio,
  EstadoAutorizacaoAr,
  ModalidadeCobrancaAr,
  ModalidadeEntregaAr,
  SituacaoCobrancaAr,
} from '@/types/arbitral/envioArTypes';

/**
 * O que a API do requerente devolve. Espelha `AutorizacaoArDto` do core — mantido
 * separado de `AutorizacaoArEnvio` de propósito: o tipo da UI nasceu do mock da
 * designer e é contrato de tela, este aqui é contrato de rede, e os dois mudam por
 * motivos diferentes.
 */
export interface AutorizacaoArDto {
  solicitacaoId: string;
  processoArbitralId: string;
  parteArbitralId: string;
  requeridoNome: string;
  rodada: number;
  estado: EstadoServidorAutorizacaoAr;
  vencida: boolean;
  modalidadeCobranca: ModalidadeCobrancaAr;
  modalidadeEntrega: ModalidadeEntregaAr;
  endereco: {
    cep: string;
    logradouro: string;
    numero: string;
    complemento: string | null;
    bairro: string;
    cidade: string;
    uf: string;
  };
  /** Strings decimais — nenhum `number` cruza a API. */
  valores: { valorEnvio: string; valorModalidade: string; valorTotal: string };
  /** Chave de calendário `YYYY-MM-DD`, não instante. */
  prazoAutorizacaoEm: string;
  decididaEm: string | null;
  pagoEm: string | null;
  observacaoRequerente: string | null;
  situacaoCobranca: SituacaoCobrancaAr;
  linkPagamento: string | null;
  acoes: AcaoAutorizacaoAr[];
}

/**
 * Estados que o SERVIDOR conhece. Note o que falta: `PROCESSANDO` e `VERIFICANDO` são
 * do navegador (checkout aberto, "Já paguei" em voo) e nunca chegam pela rede;
 * `PRAZO_VENCIDO` chega como `AGUARDANDO_AUTORIZACAO` + `vencida`, porque vencer muda
 * o TOM do card, não o que se pode fazer nele.
 */
export type EstadoServidorAutorizacaoAr =
  | 'AGUARDANDO_AUTORIZACAO'
  | 'PAGO'
  | 'AUTORIZADO_CONVENIO'
  | 'RECUSADO'
  | 'ENDERECO_EM_CORRECAO'
  | 'ERRO_EMISSAO';

export interface ResumoAutorizacoesAr {
  acionaveis: number;
  vencidas: number;
  comErroEmissao: number;
}

export interface ListaAutorizacoesAr {
  items: AutorizacaoArDto[];
  resumo: ResumoAutorizacoesAr;
  total: number;
  hasMore: boolean;
}

/**
 * DTO → o envio que a tela consome.
 *
 * Os três valores atravessam como **string decimal**, do jeito que saíram do
 * `Decimal(10,2)`. O `Number()` que existia aqui era descrito como "conversão de
 * exibição segura", e não era: além do arredondamento de ponto flutuante num valor
 * que o requerente autoriza, ele fazia o card decidir a linha do adicional por
 * `valorModalidade > 0` — régua de número sobre um dado que nunca foi número.
 * A régua correta é sobre a string, e `ehZero` já a implementa.
 */
export function comoEnvioDaTela(dto: AutorizacaoArDto): AutorizacaoArEnvio {
  return {
    solicitacaoId: dto.solicitacaoId,
    processoArbitralId: dto.processoArbitralId,
    requeridoNome: dto.requeridoNome,
    estado: dto.estado as EstadoAutorizacaoAr,
    cobranca: dto.modalidadeCobranca,
    endereco: dto.endereco,
    valores: {
      valorEnvio: dto.valores.valorEnvio,
      valorModalidade: dto.valores.valorModalidade,
      valorTotal: dto.valores.valorTotal,
    },
    modalidadeEntrega: dto.modalidadeEntrega,
    situacaoCobranca: dto.situacaoCobranca,
    // Do SERVIDOR, nunca derivada do estado: é o que separa quem pode decidir de
    // quem só lê (VIEWER recebe `[]`).
    acoes: dto.acoes ?? [],
    prazoAutorizacaoEm: dto.prazoAutorizacaoEm,
    // `decididaEm` no servidor, `respondidoEm` na tela: o vocabulário da UI nasceu do
    // mock e é o que os componentes leem. Renomear lá mexeria no contrato visual.
    respondidoEm: dto.decididaEm,
    pagoEm: dto.pagoEm,
    linkPagamento: dto.linkPagamento,
    observacaoRequerente: dto.observacaoRequerente,
    rodada: dto.rodada,
  };
}
